/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ensureAuthenticated } from '../../utilities/is-authenticated';
import { trackedAction } from '../../utilities/track-execution-time';
import { IShipToTableData, ISoldToTableData } from './get-cart.action';

/**
 * PostCreateOrder Input Action
 */
export class PostCreateOrderInput implements Msdyn365.IActionInput {
    public createOrderInput: IPostCreateOrderData;
    constructor(createOrderInput: IPostCreateOrderData) {
        this.createOrderInput = createOrderInput;
    }

    // a cache object type and an appropriate cache key
    public getCacheKey = () => `PostCreateOrder-${JSON.stringify(this.createOrderInput)}`;
    public getCacheObjectType = () => 'PostCreateOrder';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

export interface IPostCreateOrderData {
    Language: string;
    Order: IOrderData;
}

export interface IOrderData {
    Incoterm1: string;
    PurchaseOrderNumbExt: string;
    SalesOrg: string;
    DistributionChannel: string;
    Division: string;
    SoldTo: string;
    ShiptTo: string;
    OrderLines: IOrderLineData[];
    HeaderTexts: IHeaderTextsData[];
}

export interface IOrderLineData {
    ProductNumber: string;
    RequestedDate: Date;
    RequestedQty: string;
    Unit: string;
    EndCustomer: string;
}

export interface IResponseOrderLineData extends IOrderLineData {
    LineNumber: string;
    RequestedUnit: string;
    ProductName: string;
}
export interface IHeaderTextsData {
    TextId: string;
    Value: string;
    ExtensionProperties?: [];
}

export interface IPostCreateOrderResponseOrderData {
    OrderNumber: string;
    OrderType: string;
    Incoterm1: string;
    Incoterm2: string;
    PurchaseOrderNumbExt: string;
    PaymentTerms: string;
    TotalWeight: string;
    TotalUnit: string;
    OrderLines: IResponseOrderLineData[];
    ShipTo: IShipToTableData;
    SoldTo: ISoldToTableData;
    HeaderTexts: IHeaderTextsData[];
}
export interface IPostCreateOrderResponseData {
    '@odata.context': string;
    Order: IPostCreateOrderResponseOrderData;
    Messages: IPostCreateOrderResponseDataMessages[];
}

export interface IPostCreateOrderResponseDataMessages {
    ReturnCode: number;
    MessageText?: string;
    MessageReference1?: string;
    MessageReference2?: string;
}

const createInput = (_: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new PostCreateOrderInput({} as IPostCreateOrderData);
};

async function action(input: PostCreateOrderInput, ctx: Msdyn365.IActionContext): Promise<IPostCreateOrderResponseData> {
    ensureAuthenticated(ctx);
    let postCreateOrderData: IPostCreateOrderResponseData = {} as IPostCreateOrderResponseData;
    await Msdyn365.sendRequest(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/CreateOrder`,
        'post',
        {
            createOrder: input.createOrderInput
        },
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`
            }
        }
    )
        .then(res => {
            postCreateOrderData = res.data as IPostCreateOrderResponseData;
        })
        .catch(error => {
            console.error('Error creating order', error);
            throw new Error('Error creating order');
        });
    return postCreateOrderData;
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IPostCreateOrderResponseData>>((input, ctx) => trackedAction(action, input, ctx, 'CreateOrder')),
    id: 'PostCreateOrder',
    input: createInput
});
